// Fonte "Vídeo" (D1): job assíncrono. O front cria a linha em doc_ai_jobs,
// sobe o vídeo (upload resumável no bucket audit-videos, prefixo docs/) e
// aciona a edge function doc-ai-video; o worker de transcrição chama o
// webhook doc-ai-video-callback, que descreve as telas e gera o documento.
import * as tus from 'tus-js-client';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { DocAiGenerateResult, DocAiOutput } from '@/lib/docAi';
import type { DocItem } from '@/types/documentos';
import type { Json } from '@/integrations/supabase/types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const BUCKET = 'audit-videos';
const CHUNK = 6 * 1024 * 1024;

export const VIDEO_ACCEPT = '.mp4,.mov,.webm,video/mp4,video/quicktime,video/webm';
/** Sugestões (não aprovadas pela Sophia): 90 min / 2 GB. */
export const SUGGESTED_MAX_MINUTES = 90;
export const MAX_VIDEO_BYTES = 2 * 1024 * 1024 * 1024;

export type DocAiJobStatus = 'enviando' | 'transcrevendo' | 'analisando_telas' | 'gerando' | 'pronto' | 'erro';

export const JOB_STEPS: Array<{ key: DocAiJobStatus; label: string }> = [
  { key: 'enviando', label: 'Enviando' },
  { key: 'transcrevendo', label: 'Transcrevendo' },
  { key: 'analisando_telas', label: 'Analisando telas' },
  { key: 'gerando', label: 'Gerando documento' },
  { key: 'pronto', label: 'Pronto' },
];

export interface DocAiJobFrame {
  at: number;
  url: string;
  description: string;
}

export interface DocAiJob {
  id: string;
  status: DocAiJobStatus;
  fileName: string | null;
  transcript: string | null;
  frames: DocAiJobFrame[];
  result: DocAiGenerateResult | null;
  error: string | null;
  includeScreens: boolean;
  createdAt: string;
  /** Cadastro do documento (nome, tipo, pasta) para reabrir o "Criar com IA" pelo link do e-mail. */
  draft: DocItem | null;
}

/** Link que o e-mail e o aviso usam para voltar ao job (DocumentosLista lê ?iaJob=). */
export function iaJobPath(jobId: string): string {
  return `/documentos/lista?iaJob=${jobId}`;
}

function rowToJob(r: Record<string, unknown>): DocAiJob {
  return {
    id: r.id as string,
    status: r.status as DocAiJobStatus,
    fileName: (r.file_name as string | null) ?? null,
    transcript: (r.transcript as string | null) ?? null,
    frames: ((r.frames as DocAiJobFrame[] | null) ?? []),
    result: (r.result as DocAiGenerateResult | null) ?? null,
    error: (r.error as string | null) ?? null,
    includeScreens: !!r.include_screens,
    createdAt: r.created_at as string,
    draft: ((r.params as { draft?: DocItem } | null)?.draft) ?? null,
  };
}

async function accessToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) return session.access_token;
  const { data } = await supabase.auth.refreshSession();
  if (data.session?.access_token) return data.session.access_token;
  throw new Error('Sessão expirada. Faça login novamente.');
}

function tusUpload(file: File, objectName: string, token: string, onProgress: (loaded: number, total: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const up = new tus.Upload(file, {
      endpoint: `${SUPABASE_URL}/storage/v1/upload/resumable`,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      headers: { authorization: `Bearer ${token}`, apikey: SUPABASE_ANON_KEY, 'x-upsert': 'false' },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      metadata: { bucketName: BUCKET, objectName, contentType: file.type || 'video/mp4', cacheControl: '3600' },
      chunkSize: CHUNK,
      onError: reject,
      onProgress,
      onSuccess: () => resolve(),
    });
    up.findPreviousUploads().then((prev) => {
      if (prev.length) up.resumeFromPreviousUpload(prev[0]);
      up.start();
    }).catch(() => up.start());
  });
}

export interface StartVideoJobInput {
  file: File;
  output: DocAiOutput;
  structure: string[];
  typeName?: string;
  companyName?: string;
  docTitle?: string;
  includeScreens: boolean;
  /** Cadastro em andamento: volta junto quando o usuário reabre pelo e-mail. */
  draft?: DocItem | null;
  onProgress?: (pct: number) => void;
}

/** Cria o job, sobe o vídeo e dispara o processamento. Devolve o id do job. */
export async function startVideoJob(input: StartVideoJobInput): Promise<string> {
  if (input.file.size > MAX_VIDEO_BYTES) throw new Error('Vídeo acima de 2 GB (sugestão de limite, ainda a confirmar).');
  const { data: job, error } = await supabase
    .from('doc_ai_jobs')
    .insert({
      status: 'enviando',
      file_name: input.file.name,
      include_screens: input.includeScreens,
      output: input.output,
      params: {
        structure: input.structure, typeName: input.typeName, companyName: input.companyName, docTitle: input.docTitle,
        // JSON puro (DocItem só tem campos serializáveis).
        draft: input.draft ? (JSON.parse(JSON.stringify(input.draft)) as Json) : null,
      },
    })
    .select('id')
    .single();
  if (error) throw error;
  const safe = input.file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
  const objectName = `docs/${job.id}/${safe}`;
  const token = await accessToken();
  await tusUpload(input.file, objectName, token, (l, t) => input.onProgress?.(Math.round((l / t) * 100)));
  const { data, error: fnErr } = await supabase.functions.invoke('doc-ai-video', {
    body: { jobId: job.id, videoPath: objectName },
  });
  if (fnErr) throw new Error((data as { error?: string } | null)?.error || fnErr.message);
  return job.id;
}

export async function fetchVideoJob(jobId: string): Promise<DocAiJob> {
  const { data, error } = await supabase.from('doc_ai_jobs').select('*').eq('id', jobId).single();
  if (error) throw error;
  return rowToJob(data as unknown as Record<string, unknown>);
}

/** Acompanha o job a cada 4 s até terminar. */
export function useDocAiJob(jobId: string | null) {
  return useQuery({
    queryKey: ['doc-ai-job', jobId],
    enabled: !!jobId,
    queryFn: () => fetchVideoJob(jobId!),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'pronto' || s === 'erro' ? false : 4000;
    },
  });
}

/** Jobs do usuário ainda em andamento ou prontos e não consumidos (para retomar ao voltar). */
export async function listOpenVideoJobs(): Promise<DocAiJob[]> {
  const { data, error } = await supabase
    .from('doc_ai_jobs')
    .select('*')
    .is('document_id', null)
    .order('created_at', { ascending: false })
    .limit(10);
  if (error) throw error;
  return (data ?? []).map((r) => rowToJob(r as unknown as Record<string, unknown>));
}

export async function attachJobToDocument(jobId: string, documentId: string): Promise<void> {
  const { error } = await supabase.from('doc_ai_jobs').update({ document_id: documentId }).eq('id', jobId);
  if (error) throw error;
}

/** Baixa o frame (URL assinada pelo callback) como PNG para entrar no .docx. */
export async function fetchFramePng(url: string): Promise<Uint8Array> {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Não foi possível baixar o print da tela.');
  return new Uint8Array(await res.arrayBuffer());
}

export async function videoFileFromJob(job: DocAiJob): Promise<File> {
  // O vídeo já está no bucket: a Fonte referencia o path, sem baixar de novo.
  return new File([], job.fileName ?? 'video.mp4');
}

/**
 * Jobs de vídeo do usuário que ainda não viraram documento, das últimas 24 h —
 * o aviso no app compara o status entre duas leituras.
 */
export async function listRecentVideoJobs(): Promise<DocAiJob[]> {
  const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('doc_ai_jobs')
    .select('id, status, file_name, include_screens, created_at, error, params')
    .is('document_id', null)
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(20);
  if (error) throw error;
  return (data ?? []).map((r) => rowToJob(r as unknown as Record<string, unknown>));
}
